import { useLayoutEffect, useRef, useState } from "react";
import { cn, ToggleGroup, ToggleGroupItem } from "@nexus/ui";

interface SegmentedToggleOption<T extends string> {
  value: T;
  label: string;
}

interface SegmentedToggleProps<T extends string> {
  value: T;
  options: SegmentedToggleOption<T>[];
  onValueChange: (value: T) => void;
  ariaLabel: string;
}

interface IndicatorRect {
  left: number;
  width: number;
}

/**
 * A segmented control whose selection travels between options instead of
 * jumping. Radix still owns the behaviour — roving focus, arrow keys, the
 * `radio` roles — and this only takes over the painted background, so the
 * a11y contract is unchanged.
 *
 * The indicator animates `width` alongside `transform`, which the usual advice
 * forbids. It is allowed here because the element is absolutely positioned and
 * so reflows nothing but itself, and because the alternative — `scaleX` on a
 * fully rounded pill — visibly distorts the end caps.
 */
export function SegmentedToggle<T extends string>({
  value,
  options,
  onValueChange,
  ariaLabel,
}: SegmentedToggleProps<T>) {
  const itemRefs = useRef(new Map<string, HTMLButtonElement>());
  const [indicator, setIndicator] = useState<IndicatorRect | null>(null);

  // Deliberately unkeyed: the pill has to follow the active item when the
  // labels change with the locale or the header wraps, not only when the
  // selection does. The equality guard is what keeps that from looping.
  useLayoutEffect(() => {
    const node = itemRefs.current.get(value);
    if (!node) return;

    const next = { left: node.offsetLeft, width: node.offsetWidth };
    setIndicator((current) =>
      current && current.left === next.left && current.width === next.width ? current : next,
    );
  });

  return (
    <ToggleGroup
      type="single"
      size="sm"
      value={value}
      onValueChange={(next) => {
        // Radix hands back "" when the active item is pressed again; keeping
        // the current mode makes these behave as radios rather than dropping
        // the caller into an unselected state.
        if (next) onValueChange(next as T);
      }}
      aria-label={ariaLabel}
      className="relative"
    >
      {indicator ? (
        <span
          aria-hidden
          className="absolute left-0 top-0.5 h-6 rounded-full bg-primary shadow-sm transition-[transform,width] duration-200 ease-out motion-reduce:transition-none"
          style={{ transform: `translateX(${indicator.left}px)`, width: indicator.width }}
        />
      ) : null}

      {options.map((option) => (
        <ToggleGroupItem
          key={option.value}
          value={option.value}
          ref={(node) => {
            if (node) itemRefs.current.set(option.value, node);
            else itemRefs.current.delete(option.value);
          }}
          // The travelling pill is the background now, so the item keeps only
          // its text colour from the on state.
          className={cn(
            "relative z-10 h-6 text-[11px]",
            "data-[state=on]:bg-transparent data-[state=on]:shadow-none",
          )}
        >
          {option.label}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  );
}
